import { ProductLoader } from './theme.js';

/**
 * Price / badge updates on variant:change (Impact 5.4.1 — removed from theme 7.0.1).
 * Required for custom variant picker; also supports PDP elements with a form attribute.
 */
function formatMoney(cents, format = '') {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || window.themeVariables.settings.moneyFormat;

  function defaultTo(value, defaultValue) {
    return value == null || value !== value ? defaultValue : value;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, '.');
    decimal = defaultTo(decimal, ',');
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsAmount = parts[1] ? decimal + parts[1] : '';
    return dollarsAmount + centsAmount;
  }

  let value = '';
  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', '.');
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    case 'amount_no_decimals_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 0, "'");
      break;
    default:
      value = formatWithDelimiters(cents, 2);
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

const currencyFormat = window.themeVariables?.settings?.currencyCodeEnabled
  ? window.themeVariables.settings.moneyWithCurrencyFormat
  : window.themeVariables.settings.moneyFormat;

function getVariantChangeForm(element) {
  const formId = element.getAttribute('form');
  return formId ? document.forms[formId] : null;
}

function bindVariantChange(element, handler) {
  const form = getVariantChangeForm(element);
  if (!form) {
    return;
  }
  form.addEventListener('variant:change', handler);
  element._dtailsVariantChangeHandler = handler;
}

function unbindVariantChange(element) {
  const form = getVariantChangeForm(element);
  if (form && element._dtailsVariantChangeHandler) {
    form.removeEventListener('variant:change', element._dtailsVariantChangeHandler);
  }
}

function updatePriceTextContent(element, cents) {
  const srOnly = element.querySelector('.sr-only');
  const fragment = document.createRange().createContextualFragment(formatMoney(cents, currencyFormat));
  if (srOnly) {
    element.replaceChildren(srOnly, fragment);
  } else {
    element.replaceChildren(fragment);
  }
}

if (!window.customElements.get('sale-price')) {
  class SalePrice extends HTMLElement {
    connectedCallback() {
      bindVariantChange(this, this._onVariantChanged.bind(this));
    }
    disconnectedCallback() {
      unbindVariantChange(this);
    }
    _onVariantChanged(event) {
      const variant = event.detail.variant;
      if (!variant) {
        return;
      }
      updatePriceTextContent(this, variant.price);
      this.classList.toggle('text-on-sale', variant.compare_at_price > variant.price);
    }
  }
  window.customElements.define('sale-price', SalePrice);
}

if (!window.customElements.get('compare-at-price')) {
  class CompareAtPrice extends HTMLElement {
    connectedCallback() {
      bindVariantChange(this, this._onVariantChanged.bind(this));
    }
    disconnectedCallback() {
      unbindVariantChange(this);
    }
    _onVariantChanged(event) {
      const variant = event.detail.variant;
      if (!variant) {
        return;
      }
      updatePriceTextContent(this, variant.compare_at_price);
      this.toggleAttribute('hidden', !(variant.compare_at_price > variant.price));
    }
  }
  window.customElements.define('compare-at-price', CompareAtPrice);
}

if (!window.customElements.get('sold-out-badge')) {
  class SoldOutBadge extends HTMLElement {
    connectedCallback() {
      bindVariantChange(this, this._onVariantChanged.bind(this));
    }
    disconnectedCallback() {
      unbindVariantChange(this);
    }
    _onVariantChanged(event) {
      this.toggleAttribute('hidden', event.detail.variant?.available !== false);
    }
  }
  window.customElements.define('sold-out-badge', SoldOutBadge);
}

if (!window.customElements.get('on-sale-badge')) {
  class OnSaleBadge extends HTMLElement {
    connectedCallback() {
      bindVariantChange(this, this._onVariantChanged.bind(this));
    }
    disconnectedCallback() {
      unbindVariantChange(this);
    }
    _onVariantChanged(event) {
      const variant = event.detail.variant;
      if (!variant || !(variant.compare_at_price > variant.price)) {
        this.setAttribute('hidden', '');
        return;
      }
      this.removeAttribute('hidden');
      if (!this.hasAttribute('discount-mode')) {
        return;
      }
      const savings = this.getAttribute('discount-mode') === 'percentage'
        ? `${Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price)}%`
        : formatMoney(variant.compare_at_price - variant.price, currencyFormat);
      const template = window.themeVariables.strings.discountBadge;
      if (template) {
        this.innerHTML = template.replace('@@', savings);
      }
    }
  }
  window.customElements.define('on-sale-badge', OnSaleBadge);
}

if (!window.customElements.get('variant-sku')) {
  class VariantSku extends HTMLElement {
    connectedCallback() {
      bindVariantChange(this, this._onVariantChanged.bind(this));
    }
    disconnectedCallback() {
      unbindVariantChange(this);
    }
    _onVariantChanged(event) {
      const variant = event.detail.variant;
      if (!variant?.sku) {
        this.hidden = true;
        return;
      }
      const skuLabel = window.themeVariables.strings.sku || '';
      this.textContent = `${skuLabel} ${variant.sku}`.trim();
      this.hidden = false;
    }
  }
  window.customElements.define('variant-sku', VariantSku);
}

if (!window.customElements.get('variant-inventory')) {
  class VariantInventory extends HTMLElement {
    connectedCallback() {
      bindVariantChange(this, this._onVariantChanged.bind(this));
    }
    disconnectedCallback() {
      unbindVariantChange(this);
    }
    _onVariantChanged(event) {
      const inStockMessage = this.querySelector('[data-in-stock-message]');
      if (!inStockMessage) {
        return;
      }
      inStockMessage.toggleAttribute('hidden', event.detail.variant?.available !== true);
    }
  }
  window.customElements.define('variant-inventory', VariantInventory);
}

if (!window.customElements.get('payment-terms')) {
  class PaymentTerms extends HTMLElement {
    connectedCallback() {
      bindVariantChange(this, this._onVariantChanged.bind(this));
    }
    disconnectedCallback() {
      unbindVariantChange(this);
    }
    _onVariantChanged(event) {
      const variant = event.detail.variant;
      const input = this.querySelector('[name="id"]');
      if (!variant || !input) {
        return;
      }
      input.value = variant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  window.customElements.define('payment-terms', PaymentTerms);
}

/**
 * Buy buttons + Klaviyo back-in-stock on variant:change (Impact 5.4.1 — removed from theme 7.0.1).
 */
function getVariantDisplayName(variant) {
  return variant?.name || variant?.public_title || variant?.title || '';
}

function updateBuyButtonsOnVariantChange(buyButtons, variant) {
  const addToCartButton = buyButtons.querySelector('button[type="submit"]');
  if (!addToCartButton) {
    return;
  }

  const paymentButton = buyButtons.querySelector('.shopify-payment-button');
  const strings = window.themeVariables?.strings || {};
  const addToCartButtonText = addToCartButton.getAttribute('is') === 'custom-button'
    ? addToCartButton.firstElementChild
    : addToCartButton;

  addToCartButton.classList.remove('button--secondary', 'button--subdued');

  if (!variant) {
    addToCartButton.disabled = true;
    if (addToCartButtonText) {
      addToCartButtonText.innerHTML = strings.unavailableButton || '';
    }
    addToCartButton.classList.add('button--subdued');
    if (paymentButton) {
      paymentButton.style.display = 'none';
    }
    return;
  }

  addToCartButton.disabled = !variant.available;

  if (!variant.available) {
    if (addToCartButtonText) {
      addToCartButtonText.innerHTML = strings.soldOutButton || '';
    }
    addToCartButton.classList.add('button--subdued');
    if (paymentButton) {
      paymentButton.style.display = 'none';
    }
    return;
  }

  if (paymentButton || buyButtons.hasAttribute('force-secondary-button')) {
    addToCartButton.classList.add('button--secondary');
  }

  const isPreOrder = buyButtons.getAttribute('template')?.includes('pre-order');
  if (addToCartButtonText) {
    addToCartButtonText.innerHTML = isPreOrder
      ? (strings.preOrderButton || '')
      : (strings.addToCartButton || '');
  }

  if (paymentButton) {
    paymentButton.style.display = 'block';
  }
}

function updateKlaviyoBackInStock(variant, formId) {
  const klaviyoBtn = document.querySelector(`.custom-klaviyo-backinstock-btn[form="${formId}"]`)
    || document.querySelector('.custom-klaviyo-backinstock-btn');
  if (!klaviyoBtn) {
    return;
  }

  const modal = document.getElementById('backInStockModal');
  const titleEl = document.querySelector('.bis-product-title');

  if (variant?.id && modal) {
    modal.setAttribute('data-vid', variant.id);
  }

  if (titleEl && variant) {
    titleEl.innerText = getVariantDisplayName(variant);
  }

  const successEl = document.getElementById('subscriptionSuccess');
  if (successEl) {
    successEl.style.display = 'none';
  }

  if (variant?.available) {
    klaviyoBtn.classList.remove('active');
  } else {
    klaviyoBtn.classList.add('active');
  }
}

const dtailsVariantFormsInit = new Set();

function initProductFormVariantListeners() {
  document.querySelectorAll('buy-buttons[form]').forEach((buyButtons) => {
    const formId = buyButtons.getAttribute('form');
    if (!formId || dtailsVariantFormsInit.has(formId)) {
      return;
    }

    const form = document.forms[formId];
    if (!form) {
      return;
    }

    dtailsVariantFormsInit.add(formId);
    form.addEventListener('variant:change', (event) => {
      const variant = event.detail?.variant;
      document.querySelectorAll(`buy-buttons[form="${formId}"]`).forEach((element) => {
        updateBuyButtonsOnVariantChange(element, variant);
      });
      updateKlaviyoBackInStock(variant, formId);
    });
  });
}

function initKlaviyoBackInStockModal() {
  const klaviyoBtn = document.querySelector('.custom-klaviyo-backinstock-btn');
  const closeModal = document.getElementById('closeModal');
  const backInStockForm = document.getElementById('backInStockForm');

  if (!klaviyoBtn || !closeModal || !backInStockForm || klaviyoBtn.dataset.dtailsBisInit) {
    return;
  }

  klaviyoBtn.dataset.dtailsBisInit = 'true';

  klaviyoBtn.addEventListener('click', (e) => {
    e.preventDefault();
    document.getElementById('backInStockModal').style.display = 'block';
  });

  closeModal.addEventListener('click', () => {
    document.getElementById('backInStockModal').style.display = 'none';
  });

  backInStockForm.addEventListener('submit', function (event) {
    event.preventDefault();

    const email = document.getElementById('bis-email').value;
    const variantId = document.getElementById('backInStockModal').getAttribute('data-vid');
    const publicApiKey = document.getElementById('backInStockModal').getAttribute('data-key');
    const listId = document.getElementById('backInStockModal').getAttribute('data-list');
    let subscribeToList = false;
    if (document.getElementById('subscribe-list')) {
      subscribeToList = document.getElementById('subscribe-list').checked;
    }
    if (!email || !variantId || !publicApiKey) {
      alert('There was an error. Please try again.');
      return;
    }

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
              }
            }
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`
            }
          }
        }
      }
    };

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-06-15'
      },
      body: JSON.stringify(payload),
    };

    fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${publicApiKey}`, requestOptions)
      .then((result) => {
        if (result.ok) {
          document.getElementById('subscriptionSuccess').style.display = 'block';
          if (subscribeToList && listId) {
            subscribeToKlaviyoList(email, publicApiKey, listId);
          }
        } else {
          document.getElementById('subscriptionSuccess').style.display = 'none';
          alert('There was an error. Please try again later.');
        }
      })
      .catch(() => alert('There was an error. Please try again later.'));
  });

  function subscribeToKlaviyoList(email, publicApiKey, listId) {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId
            }
          }
        }
      }
    };

    fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${publicApiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: '2024-10-15.pre',
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((res) => console.log(res))
      .catch((err) => console.error(err));
  }
}

/**
 * Custom variant picker (metafield-linked products) — ported from Impact 5.4.1
 */
function protocolizeUrl(url) {
  if (typeof url === 'string' && url.startsWith('//')) {
    return window.location.protocol + url;
  }
  return url;
}

function enrichRelatedVariants(responseAsJson) {
  const relatedVariants = JSON.parse(document.querySelector('#related-variants').textContent);
  const relatedVariantsRelationship = JSON.parse(document.querySelector('#related-variants-relationship').textContent);

  relatedVariants.forEach((variant) => {
    relatedVariantsRelationship.forEach((variantRelationship) => {
      if (variant.id != variantRelationship.variantId) {
        return;
      }

      variant.product_name = variantRelationship.productTitle;
      variant.product_description = variantRelationship.productDescription;
      variant.option1 = variantRelationship.option1 ? variantRelationship.option1 : null;
      variant.option2 = variantRelationship.option2 ? variantRelationship.option2 : null;
      variant.product_url = variantRelationship.productUrl;

      if (variant.option1 != null && variant.option2 == null) {
        variant.options = [variant.option1];
      } else if (variant.option1 == null && variant.option2 != null) {
        variant.options = [variant.option2];
      } else {
        variant.options = [variant.option1, variant.option2];
      }

      if (!variant.featured_image && variantRelationship.featuredImage) {
        variant.featured_image = protocolizeUrl(variantRelationship.featuredImage);
      }
      if (variant.featured_image?.src?.startsWith?.('//')) {
        variant.featured_image.src = protocolizeUrl(variant.featured_image.src);
      }
      if (!variant.featured_media && variantRelationship.featuredMedia) {
        variant.featured_media = variantRelationship.featuredMedia;
        if (variant.featured_media.src?.startsWith?.('//')) {
          variant.featured_media.src = protocolizeUrl(variant.featured_media.src);
        }
        if (variant.featured_media.preview_image?.src?.startsWith?.('//')) {
          variant.featured_media.preview_image.src = protocolizeUrl(variant.featured_media.preview_image.src);
        }
      }
      if (variant.featured_media?.preview_image?.src?.startsWith?.('//')) {
        variant.featured_media.preview_image.src = protocolizeUrl(variant.featured_media.preview_image.src);
      }
      if (!variant.media && variantRelationship.media) {
        variant.media = variantRelationship.media;
        variant.media.forEach((media) => {
          if (media.src?.startsWith?.('//')) {
            media.src = protocolizeUrl(media.src);
          }
          if (media.preview_image?.src?.startsWith?.('//')) {
            media.preview_image.src = protocolizeUrl(media.preview_image.src);
          }
        });
      }
    });
  });

  responseAsJson.variants = relatedVariants;

  if (responseAsJson.featured_image?.startsWith?.('//')) {
    responseAsJson.featured_image = protocolizeUrl(responseAsJson.featured_image);
  }
  if (responseAsJson.images?.length) {
    for (let i = 0; i < responseAsJson.images.length; i++) {
      if (responseAsJson.images[i]?.startsWith?.('//')) {
        responseAsJson.images[i] = protocolizeUrl(responseAsJson.images[i]);
      }
    }
  }

  responseAsJson.options = JSON.parse(document.querySelector('#related-variants-options').textContent);
}

const originalProductLoaderLoad = ProductLoader.load.bind(ProductLoader);
ProductLoader.load = function (productHandle) {
  if (!productHandle) {
    return originalProductLoaderLoad(productHandle);
  }

  if (!document.querySelector('.variant-picker-custom')) {
    return originalProductLoaderLoad(productHandle);
  }

  if (ProductLoader.loadedProducts[productHandle]) {
    return ProductLoader.loadedProducts[productHandle];
  }

  const promise = originalProductLoaderLoad(productHandle).then((responseAsJson) => {
    if (document.querySelector('#related-variants')) {
      enrichRelatedVariants(responseAsJson);
    }
    return responseAsJson;
  });

  ProductLoader.loadedProducts[productHandle] = promise;
  return promise;
};

function getProductHandleFromVariant(variant) {
  if (!variant?.product_url) {
    return null;
  }
  const parts = variant.product_url.split('/products/');
  return parts[parts.length - 1]?.replace('/', '') || null;
}

function updateCustomProductGallery(gallery, variant) {
  if (!variant) {
    return;
  }

  const pHandle = getProductHandleFromVariant(variant);
  if (!pHandle) {
    return;
  }

  document.querySelectorAll('.product-gallery__thumbnail').forEach((item) => {
    if (item.getAttribute('data-handle') === pHandle) {
      item.removeAttribute('hidden');
    } else {
      item.setAttribute('hidden', '');
    }
  });

  document.querySelectorAll('.page-dots .tap-area').forEach((item) => {
    if (item.getAttribute('data-handle') === pHandle) {
      item.removeAttribute('hidden');
    } else {
      item.setAttribute('hidden', '');
    }
  });

  if (!variant.featured_media) {
    return;
  }

  const carousels = gallery.querySelectorAll('media-carousel');
  carousels.forEach((carousel) => {
    let newPos = 0;
    let newPosUpdated = false;

    carousel.querySelectorAll('.product-gallery__media').forEach((item) => {
      if (item.getAttribute('data-handle') === pHandle) {
        item.removeAttribute('hidden');
        if (variant.featured_image) {
          if (!newPosUpdated && item.getAttribute('slide-pos') && item.getAttribute('data-media-type') === 'image' && item.getAttribute('data-media-id') == variant.featured_media.id) {
            newPos = item.getAttribute('slide-pos');
            newPosUpdated = true;
          }
        } else if (!newPosUpdated && item.getAttribute('slide-pos')) {
          newPos = item.getAttribute('slide-pos');
          newPosUpdated = true;
        }
      } else {
        item.setAttribute('hidden', '');
      }
    });

    carousel.select(newPos, { animate: false });
  });
}

function updateCustomOptionLabels(variant) {
  if (!variant) {
    return;
  }

  document.querySelectorAll('[data-custom-option-value]').forEach((element) => {
    const position = parseInt(element.getAttribute('data-option-position'), 10);
    const value = variant[`option${position}`];
    if (value != null) {
      element.textContent = value.replace(/&nbsp;/g, '');
    }
  });
}

function updateCustomProductInfo(variant) {
  const pdpTitle = document.querySelector('.product-info .product-info__title');
  if (pdpTitle && variant?.product_name) {
    pdpTitle.innerText = variant.product_name;
  }

  const pdpDescription = document.querySelector('.product-info .product-info__description-content');
  if (pdpDescription && variant?.product_description) {
    pdpDescription.innerHTML = variant.product_description;
  }

  if (variant?.product_url && variant?.id) {
    const newUrl = `${variant.product_url}?variant=${variant.id}`;
    window.history.replaceState({ path: newUrl }, '', newUrl);
  }
}

var DtailsVariantPicker = class extends HTMLElement {
  async connectedCallback() {
    this._abortController = new AbortController();
    const form = document.forms[this.getAttribute('form')];
    this.masterSelector = form?.querySelector('[name="id"]') || form?.elements?.namedItem?.('id');
    this.optionSelectors = Array.from(this.querySelectorAll('[data-option-selector]'));

    if (!this.masterSelector) {
      console.warn(`The variant selector for product with handle ${this.productHandle} is not linked to any product form.`);
      return;
    }

    this.product = await ProductLoader.load(this.productHandle);
    this.optionSelectors.forEach((optionSelector) => {
      optionSelector.addEventListener('change', this._onOptionChanged.bind(this), { signal: this._abortController.signal });
    });
    this.masterSelector.addEventListener('change', this._onMasterSelectorChanged.bind(this), { signal: this._abortController.signal });
    this._updateDisableSelectors();
    this._updateFirstOptions();
    initCustomVariantGallery();
    initProductFormVariantListeners();
    this.selectVariant(this.selectedVariant?.id);
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  get selectedVariant() {
    return this._getVariantById(parseInt(this.masterSelector.value, 10));
  }

  get productHandle() {
    return this.getAttribute('handle');
  }

  get hideSoldOutVariants() {
    return this.hasAttribute('hide-sold-out-variants');
  }

  get updateUrl() {
    return this.hasAttribute('update-url');
  }

  selectVariant(id) {
    if (!this._isVariantSelectable(this._getVariantById(id))) {
      id = this._getFirstMatchingAvailableOrSelectableVariant()?.id;
    }

    const previousVariant = this.selectedVariant;
    if (previousVariant && previousVariant.id === id) {
      return;
    }

    this.masterSelector.value = id;
    this.masterSelector.dispatchEvent(new Event('change', { bubbles: true }));

    if (this.updateUrl && history.replaceState) {
      const newUrl = new URL(window.location.href);
      if (id) {
        newUrl.searchParams.set('variant', id);
      } else {
        newUrl.searchParams.delete('variant');
      }
      window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
    }

    this._updateDisableSelectors();
    this._updateFirstOptions();
    updateCustomOptionLabels(this.selectedVariant);
    updateCustomProductInfo(this.selectedVariant);

    this.masterSelector.form.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: {
        product: this.product,
        variant: this.selectedVariant,
        previousVariant
      }
    }));
  }

  _onOptionChanged(event) {
    if (!event.target.name?.startsWith('option')) {
      return;
    }
    this.selectVariant(this._getVariantFromOptions()?.id);
  }

  _onMasterSelectorChanged() {
    const options = this.selectedVariant?.options || [];
    options.forEach((value, index) => {
      const input = this.optionSelectors[index]?.querySelector(
        `input[type="radio"][name="option${index + 1}"][value="${CSS.escape(value)}"], input[type="hidden"][name="option${index + 1}"], select[name="option${index + 1}"]`
      );
      if (!input) {
        return;
      }

      let triggerChangeEvent = false;
      if (input.tagName === 'SELECT' || (input.tagName === 'INPUT' && input.type === 'hidden')) {
        triggerChangeEvent = input.value !== value;
        input.value = value;
      } else if (input.tagName === 'INPUT' && input.type === 'radio') {
        triggerChangeEvent = !input.checked && input.value === value;
        input.checked = input.value === value;
      }

      if (triggerChangeEvent) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  _getVariantById(id) {
    return this.product?.variants?.find((variant) => variant.id === id);
  }

  _getVariantFromOptions() {
    const options = this._getSelectedOptionValues();
    return this.product?.variants?.find((variant) => variant.options?.every((value, index) => value === options[index]));
  }

  _isVariantSelectable(variant) {
    if (!variant) {
      return false;
    }
    return variant.available || !this.hideSoldOutVariants && !variant.available;
  }

  _getFirstMatchingAvailableOrSelectableVariant() {
    let options = this._getSelectedOptionValues();
    let matchedVariant = null;
    let slicedCount = 0;

    do {
      options.pop();
      slicedCount += 1;
      matchedVariant = this.product.variants.find((variant) => {
        const slice = variant.options.slice(0, variant.options.length - slicedCount);
        if (this.hideSoldOutVariants) {
          return variant.available && slice.every((value, index) => value === options[index]);
        }
        return slice.every((value, index) => value === options[index]);
      });
    } while (!matchedVariant && options.length > 0);

    return matchedVariant;
  }

  _getSelectedOptionValues() {
    return this.optionSelectors.map((optionSelector) => {
      return optionSelector.querySelector('input[name^="option"][type="hidden"], input[name^="option"]:checked, select[name^="option"]')?.value;
    });
  }

  _updateDisableSelectors() {
    const selectedVariant = this.selectedVariant;
    if (!selectedVariant) {
      return;
    }
    this._updateDisableSelectorsForOptionLevel(0, selectedVariant);
  }

  _updateDisableSelectorsForOptionLevel(level, selectedVariant) {
    if (!this.optionSelectors[level]) {
      return;
    }

    const applyClassToSelector = (selector, valueIndex, available, hasAtLeastOneCombination) => {
      const optionValue = Array.from(selector.querySelectorAll('[data-option-value]'))[valueIndex];
      if (!optionValue) {
        return;
      }
      optionValue.toggleAttribute('hidden', !hasAtLeastOneCombination);
      if (this.hideSoldOutVariants) {
        optionValue.toggleAttribute('hidden', !available);
      } else {
        optionValue.classList.toggle('is-disabled', !available);
      }
    };

    const hasCombination = (variant, optionLevel, value, currentVariant) => {
      return Array.from({ length: optionLevel + 1 }, (_, i) => {
        if (i === optionLevel) {
          return variant[`option${optionLevel + 1}`] === value;
        }
        return variant[`option${i + 1}`] === currentVariant[`option${i + 1}`];
      }).every(Boolean);
    };

    this.product.options[level].values.forEach((value, valueIndex) => {
      const hasAtLeastOneCombination = this.product.variants.some((variant) => hasCombination(variant, level, value, selectedVariant));
      const hasAvailableVariant = this.product.variants.some((variant) => hasCombination(variant, level, value, selectedVariant) && variant.available);
      applyClassToSelector(this.optionSelectors[level], valueIndex, hasAvailableVariant, hasAtLeastOneCombination);
      this._updateDisableSelectorsForOptionLevel(level + 1, selectedVariant);
    });
  }

  _updateFirstOptions() {
    const selectedOptions = this._getSelectedOptionValues();
    const hasOption2 = selectedOptions.length >= 2;
    const availableFirstOptions = [];

    this.product.variants.forEach((variant) => {
      let foundVariant = false;

      if (!hasOption2 && variant.available) {
        foundVariant = true;
      } else if (hasOption2 && variant.option2 == selectedOptions[1] && (variant.option3 == null || variant.option3 == selectedOptions[2]) && variant.available) {
        foundVariant = true;
      }

      if (foundVariant) {
        availableFirstOptions.push(variant.option1);
      }
    });

    if (availableFirstOptions.length > 0) {
      this.optionSelectors[0]?.querySelectorAll('input').forEach((optionItem) => {
        if (!availableFirstOptions.includes(optionItem.value)) {
          const associatedLabel = optionItem.nextElementSibling;
          if (associatedLabel?.tagName === 'LABEL') {
            associatedLabel.classList.add('is-disabled');
          }
        }
      });
    }
  }
};

if (!window.customElements.get('dtails-variant-picker')) {
  window.customElements.define('dtails-variant-picker', DtailsVariantPicker);
}

function initCustomVariantGallery() {
  if (!document.querySelector('.variant-picker-custom')) {
    return;
  }

  document.querySelectorAll('product-gallery').forEach((gallery) => {
    const form = document.forms[gallery.getAttribute('form')];
    if (!form || form.dataset.dtailsCustomGalleryInit) {
      return;
    }
    form.dataset.dtailsCustomGalleryInit = 'true';
    form.addEventListener('variant:change', (event) => {
      if (!document.querySelector('.variant-picker-custom')) {
        return;
      }
      updateCustomProductGallery(gallery, event.detail.variant);
    });
  });
}

function sortCustomVariantSizes() {
  if (!document.querySelector('.variant-picker-custom') || !window.currentSizeArr) {
    return;
  }

  const sizeArr = window.currentSizeArr;
  const suffix = sizeArr[0].match(/[^\d\s+-]+$/)?.[0];
  const allSameSuffix = sizeArr.every((item) => item.endsWith(suffix));

  if (!allSameSuffix) {
    return;
  }

  const sortedArr = sizeArr.slice().sort((a, b) => {
    const getValue = (str) => {
      if (str.includes('-')) {
        return parseInt(str.split('-')[0], 10);
      }
      if (str.includes('+')) {
        return parseInt(str, 10) + 1;
      }
      return parseInt(str, 10);
    };
    return getValue(a) - getValue(b);
  });

  const container = document.querySelector('.variant-picker-custom [data-option-selector][option-type="size"]');
  if (!container) {
    return;
  }

  const labels = Array.from(container.querySelectorAll('label'));
  sortedArr.forEach((value, index) => {
    const matchingLabel = labels.find((label) => label.textContent.trim() === value);
    if (matchingLabel) {
      matchingLabel.style.order = index;
    }
  });

  labels.forEach((label, index) => {
    if (!sortedArr.includes(label.textContent.trim())) {
      label.style.order = sortedArr.length + index;
    }
  });
}

/**
* Dtails+ Accept terms and conditions
*/

function fnDtailsCartTerms(event, action, status) {
  var cartTerms = event.closest('form').querySelector('dtails-cart-terms');
  if (cartTerms) {
    var cartTermsDrawer = cartTerms.querySelector('[data-dtails-cart-terms-drawer]'),
        orderRecap = document.querySelector('.cart-order__recap');
    if (action == 'open') {
      cartTermsDrawer.classList.add('dtails-cart-terms-drawer--opened');
      if (orderRecap) orderRecap.style.position = "relative";
    } else if (action == 'close') {
      cartTermsDrawer.classList.remove('dtails-cart-terms-drawer--opened');
      if (orderRecap) orderRecap.style.position = "sticky";
    }

    if (status == 'accept') {
      var cartTermsDrawerCheckbox = cartTerms.querySelector('[data-dtails-cart-terms-checkbox]');
      if (cartTermsDrawerCheckbox) cartTermsDrawerCheckbox.checked = true;
      localStorage.setItem('theme:cart-terms-accepted', 'true');
    }
  }
}

var DtailsCartTerms = class extends HTMLElement {
  constructor() {
    super();
    document.addEventListener('submit', this._onSubmit);
    this.addEventListener("click", this._onClick);
    if (localStorage.getItem('theme:cart-terms-accepted') == 'true' ) {
      fnDtailsCartTerms(this, '', 'accept');
    }
  }

  _onClick(event) {
    if (event.target.closest('[data-dtails-cart-terms-drawer-link]')) {
      fnDtailsCartTerms(event.target, 'open');
      event.preventDefault();
    } else if (event.target.closest('[data-dtails-cart-terms-drawer-close]')) {
      fnDtailsCartTerms(event.target, 'close');
      event.preventDefault();
    } else if (event.target.closest('[data-dtails-cart-terms-accept-button]')) {
      fnDtailsCartTerms(event.target, 'close', 'accept');
      event.preventDefault();
    } else {
      return;
    }
  }

  _onSubmit(event) {
    const submit = event.target.querySelector('button');
    const termsCartTerms = submit.closest('form').querySelector('dtails-cart-terms');
    if (!termsCartTerms) return;

    const termsCheckbox = termsCartTerms.querySelector('[data-dtails-cart-terms-checkbox]');

    if (submit.getAttribute('name') === 'checkout' && termsCheckbox) {
      if (termsCheckbox.checked) {
        localStorage.setItem('theme:cart-terms-accepted', 'true');
      } else {
        fnDtailsCartTerms(event.target, 'open');
        submit.setAttribute("aria-busy", "false");
        event.preventDefault();
        return false;
      }
    }
  }

};
if (!window.customElements.get("dtails-cart-terms") && window.themeVariables.settings.dtailsCartTerms) {
  window.customElements.define("dtails-cart-terms", DtailsCartTerms);
}

export { DtailsCartTerms };

document.addEventListener("DOMContentLoaded", function() {
  sortCustomVariantSizes();
  initCustomVariantGallery();
  initProductFormVariantListeners();
  initKlaviyoBackInStockModal();

  // Gift wrap: refresh cart after adding giftwrap to update correct property
  document.addEventListener("cartUpdates", function() {
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  });
});

/* Sticky header */
document.addEventListener('DOMContentLoaded', () => {
  const htmlElement = document.documentElement;
  const announcementBar = document.querySelector('.announcement-bar');

  function handleScroll() {
    const scrollTop = window.scrollY;
    let shouldAddScrolledClass = false;
    if (!announcementBar) {
      if (scrollTop > 0) {
        shouldAddScrolledClass = true;
      }
    }
    else {
      const announcementBarHeight = announcementBar.offsetHeight;
      if (scrollTop >= announcementBarHeight) {
        shouldAddScrolledClass = true;
      }
    }
    htmlElement.classList.toggle('scrolled', shouldAddScrolledClass);
  }

  handleScroll();
  window.addEventListener('scroll', handleScroll);
});
/* end Sticky header */

/* Fix body stuck at opacity: 0 after back-navigation with View Transitions */
if (window.themeVariables?.settings?.showPageTransition) {
  const restoreBodyOpacity = () => {
    document.body.style.setProperty('opacity', '1', 'important');
  };

  restoreBodyOpacity();
  window.addEventListener('pageshow', restoreBodyOpacity);
}

/* Profilikon - logget ind */
const AVATAR_STYLE_ID = 'custom-avatar-style';
// Override the hardcoded box-shadow with solid #0052AF
const AVATAR_CSS = `
  .account-button__avatar {
    box-shadow: #0052AF 0px 0px 0px 1px inset !important;
  }
`;

const injectAvatarStyle = (root) => {
  if (root.getElementById(AVATAR_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = AVATAR_STYLE_ID;
  style.textContent = AVATAR_CSS;
  root.appendChild(style);
};

const setupShopifyAccount = (shopifyAccount) => {
  const root = shopifyAccount.shadowRoot;
  if (!root || shopifyAccount.dataset.avatarStyled) return;
  shopifyAccount.dataset.avatarStyled = 'true';

  injectAvatarStyle(root);
  // The component can re-render its shadow root and drop the injected style
  new MutationObserver(() => injectAvatarStyle(root)).observe(root, { childList: true });
};

customElements.whenDefined('shopify-account').then(() => {
  document.querySelectorAll('shopify-account').forEach((shopifyAccount) => {
    if (shopifyAccount.shadowRoot) {
      setupShopifyAccount(shopifyAccount);
    } else {
      requestAnimationFrame(() => setupShopifyAccount(shopifyAccount));
    }
  });
});
/* end Profilikon - logget ind */
